import { useEffect, useEffectEvent, useRef, type RefObject } from 'react';
import type { ISiteMapArea } from '@interfaces/ISiteMap';
import type { SiteMapMode } from './useEquipmentPlacement';

interface IUseSiteMapOverlayArgs {
  expanded: boolean;
  mode: SiteMapMode;
  selectedArea: ISiteMapArea | null;
  onToggleExpanded: () => void;
  /** The toolbar's Expand/Collapse button — always rendered. */
  toggleRef: RefObject<HTMLButtonElement | null>;
}

/**
 * The two lifecycle effects the fullscreen-overlay round adds on top of
 * `SiteMap`'s existing draw/place/drawer state — split out to keep
 * `SiteMap.tsx` under the 600-line soft limit (quality gate), not because
 * either is reusable elsewhere.
 *
 * - Body scroll lock while `expanded`: the card is `position: fixed` (see
 *   `.card[data-expanded]` in SiteMap.module.css), so the page behind it
 *   must stop scrolling or a wheel/touch scroll passes straight through to
 *   it. Restores whatever `document.body.style.overflow` already was (not a
 *   hardcoded `''`), so this never clobbers some OTHER feature's own lock;
 *   cleans up on collapse AND on unmount, so navigating away mid-expand
 *   can't leave the page stuck unscrollable.
 * - Esc precedence: a draw/place tool active (`mode !== 'view'`) or the area
 *   drawer open already has its OWN Esc handler (the draw-mode listener and
 *   `useEquipmentPlacement`'s own in SiteMap.tsx, Base UI's Dialog closing
 *   the drawer) — this stays out of their way so a single Esc press does
 *   exactly one thing, never both cancel-the-tool AND collapse-the-overlay.
 *   Only when neither owns Esc does this collapse it.
 * - Focus follows the overlay: expanding moves it onto the overlay's
 *   Collapse button, collapsing (button, Esc or backdrop) returns it to the
 *   Expand button. The card remounts across the portal swap, so without this
 *   focus fell to <body> either way.
 */
export function useSiteMapOverlay({ expanded, mode, selectedArea, onToggleExpanded, toggleRef }: IUseSiteMapOverlayArgs) {
  useEffect(() => {
    if (!expanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  // `mode`/`selectedArea`/`onToggleExpanded` are read, not depended on: an
  // Effect Event always sees the LATEST render's values without needing to
  // be in the effect's own deps, so the listener subscribes once per real
  // expand/collapse transition instead of on every mode or selection change.
  const onEscape = useEffectEvent((e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (mode !== 'view' || selectedArea !== null) return;
    onToggleExpanded();
  });

  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (e: KeyboardEvent) => onEscape(e);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded]);

  // Only on a transition — mounting the page must not steal focus.
  const wasExpanded = useRef(expanded);
  useEffect(() => {
    if (wasExpanded.current === expanded) return;
    wasExpanded.current = expanded;
    // Non-null: the toggle sits in the toolbar unconditionally.
    toggleRef.current!.focus();
  }, [expanded, toggleRef]);
}
